// src/lib/docx-import.ts
// Use the browser build of Mammoth (works in Vite/React frontends)
import * as mammoth from "mammoth/mammoth.browser";
import { CueNote } from "../types";

// ---------- Helpers ----------
const norm = (s: string) => (s ?? "").normalize("NFKC");
const isBlank = (s: string) => !s || !s.trim();

// Brand header patterns
const BRAND_HEADER_RE = /^\s*(?:cue\s*board|cueboard|q\s*board)(?:\s*\d+)?\s*:\s*(.+)\s*$/i;
const BRAND_HEADER_BARE_RE = /^\s*(?:cue\s*board|cueboard|q\s*board)(?:\s*\d+)?\s*:\s*$/i;

// Notes markers
const NOTES_LABEL_RE = /^notes\s*:\s*$/i;
const NOTES_INLINE_RE = /^notes\s*:\s*(.+?)\s*$/i;

// Title might have trailing “Notes” on same line
const TRAILING_NOTES_TOKEN_RE = /\s*notes\s*:\s*$/i;
const TRAILING_NOTES_WITH_CONTENT_RE = /\s*notes\s*:\s*(.+)\s*$/i;

// Treat these as list markers if they appear as text (fallback)
const LIST_MARKER_RE = /^\s*(?:-|\*|\d+\)|\d+\.)\s*/;

// Trim lead whitespace artifacts (tabs, NBSPs, thin spaces)
const LEADING_WS_RE = /^[\t \u00A0\u202F]+/;

// ---------- HTML → line stream (REVISED FOR NESTING) ----------
function htmlToLines(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: string[] = [];

  const walk = (node: Node, level = 0) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      out.push(norm(node.textContent));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'li') {
      const indent = "  ".repeat(level);
      let textContent = "";
      // Extract only the direct text of the li, ignoring children like nested uls
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          textContent += child.textContent;
        }
      }
      
      if (textContent.trim()) {
        out.push(`${indent}- ${norm(textContent).trim()}`);
      }

      // Now, recurse on child elements (like nested lists)
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child, level + 1);
        }
      }
      return; // Handled this node and its children
    }
    
    // For other block tags, just process children
    if (tag === 'p' || tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' ||
        tag === 'ul' || tag === 'ol' || tag === 'div' || tag === 'body') {
      for (const child of Array.from(el.childNodes)) {
        walk(child, level);
      }
    }
  };

  walk(doc.body);

  // Split any internal newlines to discrete lines
  const exploded: string[] = [];
  for (const line of out) {
    if (line.includes("\n")) {
      exploded.push(...line.split("\n"));
    } else {
      exploded.push(line);
    }
  }
  // Filter out any completely empty lines that might result from parsing quirks
  return exploded.filter(line => line.trim().length > 0 || line.length === 0 && exploded.length > 1);
}


// ---------- Types / State ----------
type Stage = "idle" | "header" | "notes";

/**
 * Contract:
 * 1) Title = everything after ":" on the brand header line, stopping before any trailing "Notes".
 * 2) "Notes:" starts the notes block; "Notes: content" means first note line is that content.
 * 3) Inside notes:
 * - if line begins with "- " (or looks like a list marker), it becomes a bullet ("- text"),
 * - otherwise it’s a plain line,
 * - preserve true blank lines.
 * 4) Next brand header starts a new board. Spacing/page breaks irrelevant.
 */
export async function parseDocxToNotes(file: File): Promise<CueNote[]> {
  const buf = await file.arrayBuffer();

  const { value: html } = await (mammoth as any).convertToHtml({ arrayBuffer: buf });
  let lines = htmlToLines(String(html ?? ""));

  if (!lines || lines.length === 0) {
    const { value: raw } = await (mammoth as any).extractRawText({ arrayBuffer: buf });
    const text = norm(String(raw ?? "")).replace(/\r\n?/g, "\n");
    lines = text.split("\n");
  }
  
  lines = lines.flatMap((ln) => {
    const m = ln.match(/^(.*?\S)\s+(Notes\s*:)\s*(.+)$/i);
    if (m) return [m[1], `Notes: ${m[3]}`];
    return [ln];
  });

  const notes: CueNote[] = [];
  let stage: Stage = "idle";
  let currentTitle: string | null = null;
  let bodyLines: string[] = [];

  const flush = () => {
    if (!currentTitle && bodyLines.length === 0) { stage = "idle"; return; }

    const title = (currentTitle ?? "Untitled").trim();
    
    notes.push({
      id: `docx-${crypto.randomUUID()}`,
      title,
      body_markdown: bodyLines.join("\n").trim(),
      triggers: []
    });

    stage = "idle";
    currentTitle = null;
    bodyLines = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i] ?? "";
    const line = rawLine.trim();

    if (isBlank(rawLine) && stage === "notes") {
      bodyLines.push("");
      continue;
    }
    if (isBlank(line)) {
      continue;
    }

    const mHeader = line.match(BRAND_HEADER_RE);
    if (mHeader) {
      flush();
      let titlePart = (mHeader[1] ?? "").trim();

      const withContent = titlePart.match(TRAILING_NOTES_WITH_CONTENT_RE);
      if (withContent) {
        currentTitle = titlePart.replace(TRAILING_NOTES_WITH_CONTENT_RE, "").trim();
        stage = "notes";
        const first = (withContent[1] ?? "").trim();
        if (first) {
            bodyLines.push(first.trim().startsWith("-") ? first.trim() : `- ${first.trim()}`);
        }
        continue;
      }
      if (TRAILING_NOTES_TOKEN_RE.test(titlePart)) {
        currentTitle = titlePart.replace(TRAILING_NOTES_TOKEN_RE, "").trim();
        stage = "notes";
        continue;
      }

      currentTitle = titlePart || "Untitled";
      stage = "header";
      continue;
    }

    if (BRAND_HEADER_BARE_RE.test(line)) {
      flush();
      currentTitle = "Untitled";
      stage = "header";
      continue;
    }

    if (stage === "idle" || stage === "header") {
      if (NOTES_LABEL_RE.test(line)) {
        if (!currentTitle) currentTitle = "Untitled";
        stage = "notes";
        continue;
      }
      const mNotesInline = line.match(NOTES_INLINE_RE);
      if (mNotesInline) {
        if (!currentTitle) currentTitle = "Untitled";
        stage = "notes";
        const first = (mNotesInline[1] ?? "").trim();
        if (first) {
            bodyLines.push(first.trim().startsWith("-") ? first.trim() : `- ${first.trim()}`);
        }
        continue;
      }
      continue;
    }

    if (stage === "notes") {
      if (BRAND_HEADER_RE.test(line) || BRAND_HEADER_BARE_RE.test(line)) {
        flush();
        i--;
        continue;
      }
      if (NOTES_LABEL_RE.test(line) || NOTES_INLINE_RE.test(line)) continue;
      
      // Push the raw line from our new parser to preserve indentation
      bodyLines.push(rawLine);
      continue;
    }
  }

  flush();

  return notes.map(n => ({
    ...n,
    id: n.id && n.id.trim() ? n.id : `docx-${crypto.randomUUID()}`,
    title: (n.title ?? "").trim(),
    body_markdown: typeof n.body_markdown === "string" ? n.body_markdown.trim() : "",
    triggers: Array.isArray(n.triggers) ? n.triggers : []
  }));
}